import { useState } from "react";
import LocationSearch from "./LocationSearch";
import { actionProps } from "@/lib/controlAnalytics";
import { Link, useLocation } from "react-router-dom";
import { GlobalSearch } from "@/components/layout/Topbar";
import { ArrowRightIcon } from "@/components/icons";
import HeroFill from "@/components/site/HeroFill";
import { useGate } from "@/components/site/gateContext";
import { GRAD_BUTTON } from "@/components/site/styles";
import { HERO_ACTION, HERO_FACTS, HERO_LINES, HERO_SUMMARY, SEARCH_HEADLINE } from "@/lib/landing";
import { isJobsPath, ROUTES } from "@/lib/routes";

/**
 * One discovery destination, with search available at every width.
 * Signed out, the hero: Less typing, More applying, the summary, Get free
 * autofill and what it costs, beside the example of a resume filling an
 * application (under it on a phone); then a rule, Find your next role and
 * the search, its two fields labelled. While the sign-in is still read,
 * the search alone.
 */
export default function JobsHeader({
    standing = "member",
}: {
    standing?: "visitor" | "member" | "unknown";
}) {
    const { ask } = useGate();
    const { pathname } = useLocation();
    const [draft, setDraft] = useState<string | undefined>(undefined);
    const target = isJobsPath(pathname) ? pathname : ROUTES.HOME;
    if (standing !== "member") {
        const searching = (
            <LocationSearch searchWords={draft} target={target} variant="site">
                <GlobalSearch inline labelled onDraftChange={setDraft} />
            </LocationSearch>
        );
        if (standing === "unknown") return searching;
        return (
            <div className="flex flex-col gap-10 max-[760px]:gap-8">
                <section
                    aria-labelledby="hero-heading"
                    className="relative isolate grid items-center gap-10 pt-4 min-[1000px]:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] max-[760px]:gap-8 max-[760px]:pt-2"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -inset-y-16 inset-x-0 -z-10 bg-hero-glow"
                    />
                    <div className="flex min-w-0 flex-col gap-6">
                        <h1
                            id="hero-heading"
                            className="m-0 font-display text-[68px] font-extrabold leading-[0.98] tracking-[-0.045em] max-[1200px]:text-[60px] max-[760px]:text-[46px]"
                        >
                            <span className="block text-fg">{HERO_LINES[0]}</span>{" "}
                            <span className="block text-brand-ink">{HERO_LINES[1]}</span>
                        </h1>
                        <p className="m-0 max-w-[30em] text-[19px] leading-[1.45] text-fg-muted max-[760px]:text-[17px]">
                            {HERO_SUMMARY}
                        </p>
                        <div className="flex flex-col items-center gap-3 self-start max-[760px]:self-stretch">
                            <button
                                {...actionProps("join_signup_clicked", { source: "JobsHeader" })}
                                type="button"
                                onClick={() => ask("join")}
                                className={`${GRAD_BUTTON} h-14 w-full gap-2.5 rounded-[16px] px-10 text-[18px]`}
                            >
                                {HERO_ACTION}
                                <ArrowRightIcon className="size-5" strokeWidth={2.2} />
                            </button>
                            <p className="m-0 text-center text-[13px] text-fg-muted">
                                {HERO_FACTS.join(" · ")}
                            </p>
                        </div>
                    </div>
                    <HeroFill />
                </section>
                <section aria-labelledby="search-heading" className="flex flex-col gap-5">
                    <hr className="m-0 border-0 border-t border-line" />
                    <h2
                        id="search-heading"
                        className="m-0 font-display text-[36px] font-extrabold tracking-[-0.035em] max-[760px]:text-[30px]"
                    >
                        {SEARCH_HEADLINE}
                    </h2>
                    {searching}
                </section>
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h1 className="m-0 font-display text-[34px] font-extrabold tracking-[-0.03em]">
                    Jobs
                </h1>
                <Link
                    {...actionProps("envoy_configure_clicked", { source: "JobsHeader" })}
                    to={ROUTES.ENVOY}
                    className="text-[13px] font-semibold text-brand-ink"
                >
                    Match preferences
                </Link>
            </div>
            <LocationSearch searchWords={draft} target={target}>
                <GlobalSearch inline onDraftChange={setDraft} />
            </LocationSearch>
        </div>
    );
}
